import { Box, Chip, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// Chip small = 24px + 2px de margem em cima e embaixo (tagSizeSmall do
// Autocomplete) = 28px por linha. Acima de 4,5 linhas os chips rolam; a meia
// linha visível indica que há mais embaixo.
const TAG_ROW_HEIGHT = 28;
const MAX_VISIBLE_TAG_ROWS = 4.5;

const useStyles = makeStyles((theme) => ({
  tagsScroll: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexWrap: "wrap",
    maxHeight: ({ maxVisibleRows }) => TAG_ROW_HEIGHT * maxVisibleRows,
    overflowY: "auto",
    // Faixa reservada para o scrollbar: com scrollbar sobreposto (Windows 11 /
    // overlay) ele não ocupa espaço e cobria o "x" do chip mais largo, que vai
    // até 100% da linha.
    paddingRight: 14,
    ...theme.scrollbarStyles,
  },
}));

// `selectedIds` (opcional) torna o filtro controlado pelo chamador — necessário
// quando a seleção é restaurada do localStorage, senão o filtro ficava aplicado
// com os chips vazios. Sem ele, o componente guarda a seleção sozinho.
// `compact`: sem o recuo de 10px, com os mesmos 4px verticais dos selects de
// fila/conexão do popover de filtros (FormControl margin="dense").
// `label`/`variant` (opcionais): para telas cujos campos têm rótulo ou não são
// outlined (Conferência, Relatório de Mensagens).
// `maxVisibleRows`: linhas de chips antes de rolar (use x,5 para a meia linha).
export function TagsFilter({
  onFiltered,
  selectedIds,
  placeholder,
  label,
  variant = "outlined",
  compact = false,
  maxVisibleRows = MAX_VISIBLE_TAG_ROWS,
}) {
  const classes = useStyles({ maxVisibleRows });
  const [tags, setTags] = useState([]);
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [selecteds, setSelecteds] = useState([]);
  const prunedRef = useRef(false);
  const tagsScrollRef = useRef(null);
  const tagsScrollTopRef = useRef(0);

  const isControlled = Array.isArray(selectedIds);

  useEffect(() => {
    async function fetchData() {
      await loadTags();
    }
    fetchData();
  }, []);

  const loadTags = async () => {
    try {
      const { data } = await api.get(`/tags/list`);
      setTags(data);
      setTagsLoaded(true);
    } catch (err) {
      toastError(err);
    }
  };

  const value = useMemo(() => {
    if (!isControlled) return selecteds;
    const ids = selectedIds.map((id) => Number(id));
    return tags.filter((tag) => ids.includes(Number(tag.id)));
  }, [isControlled, selectedIds, selecteds, tags]);

  // Tag salva que foi apagada: tira da seleção uma única vez, para o filtro não
  // ficar aplicado sem chip visível.
  useEffect(() => {
    if (!isControlled || !tagsLoaded || prunedRef.current) return;
    prunedRef.current = true;
    if (value.length !== selectedIds.length) {
      onFiltered(value);
    }
  }, [isControlled, tagsLoaded, value, selectedIds, onFiltered]);

  // Remover um chip não pode devolver a lista ao topo: reaplica a posição
  // guardada antes da pintura (o navegador só limita ao novo máximo).
  useLayoutEffect(() => {
    if (value.length === 0) {
      // Sem chips o contêiner some; a próxima seleção começa do topo.
      tagsScrollTopRef.current = 0;
      return;
    }
    if (tagsScrollRef.current) {
      tagsScrollRef.current.scrollTop = tagsScrollTopRef.current;
    }
  }, [value]);

  const onChange = async (newValue) => {
    if (!isControlled) {
      setSelecteds(newValue);
    }
    onFiltered(newValue);
  };

  return (
    <Box style={compact ? { width: "100%", margin: "4px 0" } : { padding: 10 }}>
      <Autocomplete
        multiple
        fullWidth
        size="small"
        options={tags}
        value={value}
        onChange={(e, v, r) => onChange(v)}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, selected) => option.id === selected.id}
        renderTags={(value, getTagProps) => (
          <div
            ref={tagsScrollRef}
            className={classes.tagsScroll}
            onScroll={(e) => {
              tagsScrollTopRef.current = e.currentTarget.scrollTop;
            }}
          >
            {value.map((option, index) => (
              <Chip
                variant="outlined"
                style={{
                  backgroundColor: option.color || "#eee",
                  textShadow: "1px 1px 1px #000",
                  color: "white",
                }}
                label={option.name}
                {...getTagProps({ index })}
                size="small"
              />
            ))}
          </div>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={variant}
            label={label}
            placeholder={placeholder || "Filtro por Tags"}
          />
        )}
      />
    </Box>
  );
}
